import React, { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { glyphs, frames, colors } from "../../../Styles";
import { NewInput, PageHeader, AssignUserModal, DeleteUserModal, UserGroupPopover } from "../../../Components";
import { ButtonTypes, ListData_ValueTypes, NewFieldStyles, NewInputHandles, TableStyles_Types } from "../../../Definitions";
import { useQuery } from "@apollo/client";
import { AllUser_Data, ALLUSER_INFO, AllUserInfoResponse } from "../../../Services/Agrisys_GqlDataCaller/UserCalls/GQLCalls/userCalls";

// Table
import { ColumnFiltersState, FilterFn, getCoreRowModel, getFilteredRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table";
import { ReactiveTable } from "../../../Components/Tables/reactiveTableRenderer";
import { useColumnConstructor } from "../../../Components/Tables/_Tables/useColumnConstructor";
import { NewButtonList } from "../../../Components/BaseComponents/newButton";

type UserListViewBlockProps = {
  onView: (userId: string) => void;
};

const user_groups = {
  nameMap: {
    free: "Owner",
    worker: "Worker",
  } as Record<string, string>,
};

// function getNameOptions() {
//   return Object.entries(user_groups.nameMap).map(([key, value]) => ({ label: value, id: key }));
// }

export default function Users({ onView }: UserListViewBlockProps) {
  localStorage.setItem("activedomain", "");
  const { t } = useTranslation();
  const searchRef = useRef<NewInputHandles>(null);
  const { data, loading, error, refetch} = useQuery<AllUserInfoResponse>(ALLUSER_INFO);
  const [ globalFilterValue, setGlobalFilterValue ] = useState<string>("");
  const [ columnFilters, setColumnFilters ] = useState<ColumnFiltersState>([]);
  const [ companyGroupAddModal, setCompanyGroupAddModal ] = useState<boolean>(false);
  const [ userDeleteModal, setUserDeleteModal ] = useState<boolean>(false);
    
  // #region | Table Data
  const tableData = useMemo(() => {
    if (!data || !data.user?.allUsers) return []; // fallback

    return data.user.allUsers.map((user: AllUser_Data) => {
      const companyGroups = user.user_companies?.slice().sort((a, b) => a.name.localeCompare(b.name)).map(company => {
        const groupsForCompany = user.user_groups?.filter(g => g.fk_company === company.company_id).map(g => user_groups.nameMap[g.name] ?? g.name);

        return `${company.name}: ${groupsForCompany && groupsForCompany.length > 0 ? groupsForCompany.join(", ") : "No groups"}`}) || [];
        let companyGroupsCell: React.ReactNode;
      
        if (companyGroups.length <= 1) {
          companyGroupsCell = (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span>{companyGroups[0] || "No groups"}</span>
            </div>
          );
        } else {
          companyGroupsCell = (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span>{companyGroups[0]}</span>
              <UserGroupPopover userGroupName="Company Groups" userGroupNote={companyGroups.join("\n")} glyphColor={colors.greyDarkPrimary} />
            </div>
          );
        }
      
        return {
          id: user.user_id,
          user_id: user.user_id,
          name: (user.firstname || "") + " " + (user.lastname || ""),
          companyGroups: companyGroupsCell, 
          companyGroupsText: companyGroups.join(" "),
          email: user.email || "",
          address: user.address || "",
          phone: user.phone || "",
          username: user.username || "",
        };
    });
  }, [data]);
  // #endregion

  // #region | Table Construction
  const columnsData = useMemo(() => [
    { headerValue: "ID", accessor: "id", type: ListData_ValueTypes.String, sort: true },
    { headerValue: "Username", accessor: "username", type: ListData_ValueTypes.String, sort: true },
    { headerValue: "Company & Groups", accessor: "companyGroups", type: ListData_ValueTypes.Div, sort: false },
    { headerValue: "Name", accessor: "name", type: ListData_ValueTypes.String, sort: true },
    { headerValue: "Email", accessor: "email", type: ListData_ValueTypes.String, sort: true },
    { headerValue: "Address", accessor: "address", type: ListData_ValueTypes.String, sort: true },
    { headerValue: "Phone", accessor: "phone", type: ListData_ValueTypes.String, sort: true },
    { headerValue: "CompanyGroupsText", accessor: "companyGroupsText", type: ListData_ValueTypes.String, hidden: true, sort: false },
  ], [onView]);

  const columns = useColumnConstructor({
    accessorData: columnsData,
    tableStyle: TableStyles_Types.primaryTable,
    canEdit: false
  });

  const globalFilterFn: FilterFn<any> = (row, _columnId, filterValue) => {
  const search = filterValue.toLowerCase();

  return row.getAllCells().some(cell => {
    const value = cell.getValue();
    return typeof value === "string" && value.toLowerCase().includes(search);
  })};
  
  const table = useReactTable({
    columns,
    data: tableData,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnFiltersChange: setColumnFilters,
    globalFilterFn,
    onGlobalFilterChange: setGlobalFilterValue,
    state: {
      globalFilter: globalFilterValue,
      columnFilters,
      columnVisibility: { companyGroupsText: false },
    },
    initialState: {
      sorting: [{ id: "username", desc: false }], 
    },
  });
  // #endregion

  return ( <>
    <frames.Page_inner>
        <PageHeader topHeader="Users" subHeader="Overview" />
      <frames._page_inner_content>
        <frames._grid_topBtns_outer>
          <frames._grid_topBtns_inner style={{position: "relative", justifyContent: "end"}}>
            <div style={{ position: "absolute", top: "50%", transform: "translateY(-50%)", left: "20px" }}>
              <NewInput
                ref={searchRef}
                onChange={(value: string) => setGlobalFilterValue(value)}
                fieldOverrides={{ fieldStyle: NewFieldStyles.greySecondary }}
                placeholder={t("search", { defaultValue: "search" })}
              />
            </div>
            <NewButtonList
              contents={[
                t("buttons.assign", { defaultValue: "Assign" }),
                t("buttons.remove", { defaultValue: "Remove" }),
              ]}
              onClick={(i) => {
                if (i === 0) setCompanyGroupAddModal(true);
                if (i === 1) setUserDeleteModal(true);
              }}
              buttonType={ButtonTypes.tableButton}
              startEnhancers={[glyphs.User({}), glyphs.Delete({})]}
            />
          </frames._grid_topBtns_inner>

          <div style={{gridArea: "2/1/3/2", border: "5px solid "+colors.greyPrimary, overflow: "hidden", borderRadius: "10px", backgroundColor: colors.greyLightTertiary }}>
            {loading ?
              <div>Loading users...</div>
              : error ? <div style={{ color: "#aa0000" }}>Error loading users</div>
              : <ReactiveTable
                  table={table}
                  tableType={TableStyles_Types.primaryTable}
                  emptyText="No users found"
                  renderFooter={false}
                />
            }
          </div>
        </frames._grid_topBtns_outer>
      </frames._page_inner_content>
    </frames.Page_inner>

    <AssignUserModal
      isModalOpen={companyGroupAddModal}
      closeModal={() => setCompanyGroupAddModal(false)}
      userlist={data?.user?.allUsers.map(u => ({ ...u, firstname: u.firstname ?? "", lastname: u.lastname ?? "" })) ?? []}
      onUserAdded={() => refetch()}
    />
    
    <DeleteUserModal
      isModalOpen={userDeleteModal}
      closeModal={() => setUserDeleteModal(false)}
      userlist={data?.user?.allUsers.map(u => ({ ...u, firstname: u.firstname ?? "", lastname: u.lastname ?? "" })) ?? []}
      onUserDeleted={() => refetch()}
    />
  </> );
}


